import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../api/axios";
import { useBusinessContext, businessError } from "../BusinessContext";

export function useSelectedRecord(records, idKey, setActive) {
  const [params] = useSearchParams();
  const selected = params.get("selected") || params.get("select");
  const rowRef = useRef(null);
  const previous = useRef(null);
  const record = selected ? records.find(item => String(item[idKey]) === selected) : null;
  useEffect(() => {
    if (selected) setActive(record || null);
    else if (previous.current) setActive(null);
    previous.current = selected;
  }, [selected, record, setActive]);
  useEffect(() => {
    if (record) rowRef.current?.scrollIntoView?.({ block: "nearest" });
  }, [record]);
  const targetRef = useCallback(node => { rowRef.current = node; }, []);
  return { selected, targetRef };
}

// Apply a linked history entry's year before loading its scoped list.
export function useTargetList(url, idKey, setRecords) {
  const [params, setParams] = useSearchParams();
  const selected = params.get("selected") || params.get("select");
  const targetYear = selected ? params.get("betriebsjahr_id") : null;
  const [notice, setNotice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const { ready, withContext, betriebsjahrId, betriebsjahre, selectBetriebsjahr } = useBusinessContext();
  const validTargetYear = targetYear && betriebsjahre?.find(year => String(year.Id) === targetYear);
  useEffect(() => {
    if (!ready || !validTargetYear) return;
    if (String(betriebsjahrId) !== targetYear) selectBetriebsjahr(validTargetYear.Id);
    // Consume the navigation hint so later manual year changes stay possible.
    setParams(current => {
      const next = new URLSearchParams(current);
      next.delete("betriebsjahr_id");
      return next;
    }, { replace: true });
  }, [ready, validTargetYear, targetYear, betriebsjahrId, selectBetriebsjahr, setParams]);
  useEffect(() => {
    let cancelled = false;
    setRecords([]);
    setNotice(null);
    setLoading(Boolean(ready && !targetYear));
    const load = async () => {
      let rows = [];
      try {
        if (!ready) return;
        if (targetYear) {
          if (!validTargetYear) {
            if (!cancelled) setNotice({ severity: "warning", text: "Das Betriebsjahr des verlinkten Datensatzes ist im ausgewählten Betrieb nicht verfügbar." });
            return;
          }
          return;
        }
        const response = await api.get(withContext(url));
        rows = Array.isArray(response.data) ? response.data : [];
        let message = null;
        if (selected && !rows.some(item => String(item[idKey]) === selected)) {
          const targetResponse = await api.get(withContext(`${url}?id=${encodeURIComponent(selected)}`));
          const target = (Array.isArray(targetResponse.data) ? targetResponse.data : [])
            .find(item => String(item[idKey]) === selected);
          if (target) {
            rows = [...rows, target];
            message = { severity: "info", text: "Das verlinkte Ziel wird zusätzlich zur aktuellen Jagdjahr-/Listenansicht angezeigt." };
          } else {
            message = { severity: "warning", text: "Der verlinkte Datensatz wurde nicht gefunden oder gehört nicht zum ausgewählten Betriebsjahr." };
          }
        }
        if (!cancelled) { setRecords(rows); setNotice(message); }
      } catch (error) {
        if (!cancelled) {
          setRecords(rows);
          setNotice({ severity: "error", text: selected
            ? "Der verlinkte Datensatz konnte nicht geladen werden."
            : businessError(error, "Die Liste konnte nicht geladen werden.") });
        }
      } finally { if (!cancelled) setLoading(false); }
    };
    load();
    return () => { cancelled = true; };
  }, [url, ready, withContext, selected, idKey, setRecords, targetYear, validTargetYear, betriebsjahrId, attempt]);
  return { notice, loading, retry: () => setAttempt(value => value + 1) };
}
